import { Button, createForm, Flex, Text, TextInput, TextInputProps, useToasts } from '@applyboard/crystal-ui';
import { ComponentType, useState } from 'react';

type EditSingleFieldProps = Readonly<{
  label: string;
  value?: string;
  isLoading?: boolean;
  onSave: (fieldValue: string) => Promise<void>;
  validate: (fieldValue: string) => string | true;
}>;

type EditSingleFieldField = {
  fieldValue: string;
};

const { Form, Field, useFieldValues, useSetFieldValues } = createForm<EditSingleFieldField>();

export function EditSingleField(props: EditSingleFieldProps) {
  const toast = useToasts();
  const [disabled, setDisabled] = useState<boolean>(!!props.value);
  const [currentValue, setCurrentValue] = useState<string>(props.value ?? '');
  const { label } = props;

  return (
    <Flex direction="column" aria-describedby={label}>
      <Form
        defaultValues={{ fieldValue: currentValue }}
        onSubmit={(data) => {
          props
            .onSave(data.fieldValue)
            .then(() => {
              setDisabled(true);
              setCurrentValue(data.fieldValue);
            })
            .catch((error) => {
              toast.negative(error);
            });
        }}
      >
        <Flex gap={2} align="start">
          <Flex basis="226px">
            <Field
              as={TextInput as ComponentType<TextInputProps>}
              name="fieldValue"
              disabled={disabled ?? props.isLoading}
              validate={props.validate}
              label={
                <Text id={label} variant="labelS" contrast="mid">
                  {label.toUpperCase()}
                </Text>
              }
            />
          </Flex>
          <Flex pt={8}>
            {disabled ? (
              <Button
                intent="secondary"
                emphasis="highlighted"
                onClick={() => setDisabled(false)}
                disabled={props.isLoading}
              >
                Edit
              </Button>
            ) : (
              <Flex gap={2} wrap={false}>
                <Button intent="secondary" emphasis="highlighted" type="submit" loading={props.isLoading}>
                  Save
                </Button>
                <EditSingleFieldCancelButton
                  setDisabled={setDisabled}
                  isLoading={props.isLoading}
                  currentValue={currentValue}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Form>
    </Flex>
  );
}

type EditSingleFieldCancelButtonProps = Readonly<{
  setDisabled: (disabled: boolean) => void;
  isLoading?: boolean;
  currentValue: string;
}>;

function EditSingleFieldCancelButton({ setDisabled, isLoading, currentValue }: EditSingleFieldCancelButtonProps) {
  const { fieldValue } = useFieldValues(['fieldValue']);
  const setFieldValues = useSetFieldValues();

  if (fieldValue === '') {
    return null;
  }

  return (
    <Button
      intent="secondary"
      emphasis="transparent"
      disabled={isLoading}
      onClick={() => {
        setFieldValues({ fieldValue: currentValue });
        setDisabled(true);
      }}
    >
      Cancel
    </Button>
  );
}
